"use client";
import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTxIndexer } from "@/indexer/provider/context";
import { ContractTransactionResponse } from "ethers";
import useWithCorrectNetwork from "@/shared/hooks/useWithCorrectNetwork";
import { NftCompletePurchaseDialog } from "../NftCompletePurchaseDialog";
import useAcceptOffer from "@/web3/contracts/Exchange/actions/useAcceptOffer";
import { useOrderApprovalMutation } from "@/web3/contracts/Exchange/actions/useOrderApproval";
import { OfferWithNft } from "../../User/OffersTable/types";
import useOrdersInvalidation from "@/shared/hooks/useOrdersInvalidation";
import useWithFundsCheck from "@/shared/hooks/useWithFundsCheck";
import { useOfferSellerGet } from "../../Sales/Forms/CreateFixedAuction/hooks/useSellerGet";

export interface Props {
  offer: OfferWithNft;
  onSuccess: () => void;
  onClose: () => void;
}

function NftAcceptOfferModal({ offer, onSuccess, onClose }: Props) {
  const { startIndexerSync } = useTxIndexer();

  const [isLoading, setIsLoading] = useState(false);

  const invalidateOrders = useOrdersInvalidation(offer.nft!.id);

  const acceptOffer = useAcceptOffer(offer);

  const onIndexed = async () => {
    await invalidateOrders(1000);
    setIsLoading(false);
    onSuccess();
  };

  const { mutateAsync: settleAccept } = useMutation({
    mutationFn: acceptOffer,
    onSuccess: (tx: ContractTransactionResponse) => {
      startIndexerSync({
        txHash: tx.hash,
        onIndexed,
      });
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const { approveAll } = useOrderApprovalMutation(offer.nftContract, {
    onError: () => {
      setIsLoading(false);
    },
  });

  const onAccept = async () => {
    setIsLoading(true);

    await approveAll();
    await settleAccept();
  };

  const networkCheckedOnAccept = useWithCorrectNetwork(onAccept);

  const { checkedFn: checkedOnAccept, error } = useWithFundsCheck(
    networkCheckedOnAccept,
    offer,
  );

  const sellerGet = useOfferSellerGet(offer);

  return (
    <NftCompletePurchaseDialog
      title="Accept offer"
      sellerGet={sellerGet}
      nft={offer.nft!}
      order={offer}
      onPurchase={checkedOnAccept}
      onClose={onClose}
      buttonText="Accept offer"
      isLoading={isLoading}
      shouldShowBalance={false}
      error={error}
      displaySellerFees
    />
  );
}

export default NftAcceptOfferModal;
